import {Injectable} from '@angular/core';
import {CoverageRecommendationService, ProtectionApiService} from '@emma-labs/shared/angular';
import {
  CategoryCode,
  Coverage,
  Duration,
  getMonthlyPrice,
  HUMANIA_EMMA_V2_MAX_COVERAGE,
  isWholeLife,
  priceToRevenueRatioTooHigh,
  Protection,
  ProviderId,
  Sex,
  Version,
} from '@emma-labs/shared/typescript';
import {first, mergeMap, Observable, of, switchMap} from 'rxjs';
import {map} from 'rxjs/operators';
import {AuthService} from '../auth.service';

const EMMA_PRO_RECOMMENDED_WHOLE_LIFE_COVERAGE = 25_000;

@Injectable({
  providedIn: 'root',
})
export class ProtectionRecommendationService {
  constructor(
    private readonly protectionApiService: ProtectionApiService,
    private readonly authService: AuthService,
    private readonly coverageRecommendationService: CoverageRecommendationService,
  ) {}

  getRecommendedProtection(
    ratesVersion: Version,
    providerId: ProviderId,
    sex: Sex,
    smoker: boolean,
    age: number,
    temporaryNeeds: number,
    permanentNeeds: number,
    needs: number,
    cancerNeeds: number,
    salary: number,
    numberOfChildren: number,
    categoryCode: CategoryCode,
    youngestChildAgeInYears?: number,
  ): Observable<Protection> {
    return this.getProtection(
      ratesVersion,
      providerId,
      sex,
      smoker,
      age,
      this.getRecommendedCoverage(
        providerId,
        age,
        temporaryNeeds,
        permanentNeeds,
        needs,
        cancerNeeds,
        numberOfChildren,
        categoryCode,
        false,
        youngestChildAgeInYears,
      ),
      categoryCode,
    ).pipe(
      first(),
      mergeMap((protection) => {
        if (priceToRevenueRatioTooHigh(getMonthlyPrice(protection.personInsurances), salary)) {
          return this.getProtection(
            ratesVersion,
            providerId,
            sex,
            smoker,
            age,
            this.getRecommendedCoverage(
              providerId,
              age,
              temporaryNeeds,
              permanentNeeds,
              needs,
              cancerNeeds,
              numberOfChildren,
              categoryCode,
              true,
              youngestChildAgeInYears,
            ),
            categoryCode,
          );
        }
        return of(protection);
      }),
    );
  }

  private getProtection(
    ratesVersion: Version,
    providerId: ProviderId,
    sex: Sex,
    smoker: boolean,
    age: number,
    coverage: Coverage,
    categoryCode: CategoryCode,
  ): Observable<Protection> {
    const protectionConfig = ProtectionApiService.buildProtectionConfig(
      ratesVersion,
      providerId,
      [categoryCode],
      age,
      sex,
      smoker,
      coverage,
    );

    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      switchMap((session) =>
        this.protectionApiService.getProtections(session.id!, {
          protectionConfigs: [protectionConfig],
        }),
      ),
      map(({protections}) => protections[0]),
    );
  }

  private getRecommendedCoverage(
    providerId: ProviderId,
    age: number,
    temporaryNeeds: number,
    permanentNeeds: number,
    needs: number,
    cancerNeeds: number,
    numberOfChildren: number,
    categoryCode: CategoryCode,
    isPriceToRevenueRatioTooHigh: boolean,
    youngestChildAgeInYears?: number,
  ): Coverage {
    switch (providerId) {
      case ProviderId.HUMANIA_EMMA_V2: {
        const coverage = this.coverageRecommendationService.getRecommendedCoverage(
          age,
          temporaryNeeds,
          permanentNeeds,
          needs,
          cancerNeeds,
          numberOfChildren,
          categoryCode,
          isPriceToRevenueRatioTooHigh,
          youngestChildAgeInYears,
          false,
        );

        const hasWholeLifeInsurance = !!coverage.lifeInsurances[Duration.P100];
        if (!hasWholeLifeInsurance) {
          coverage.lifeInsurances[Duration.P100] = EMMA_PRO_RECOMMENDED_WHOLE_LIFE_COVERAGE;

          const termDuration = Object.keys(coverage.lifeInsurances).find((d) => !isWholeLife(d));
          if (termDuration) {
            const amountToDeductFromTerm =
              EMMA_PRO_RECOMMENDED_WHOLE_LIFE_COVERAGE -
              (HUMANIA_EMMA_V2_MAX_COVERAGE - coverage.lifeInsurances[termDuration]);

            coverage.lifeInsurances[termDuration] -= Math.max(0, amountToDeductFromTerm);
          }
        }
        return coverage;
      }
      default: {
        throw new Error('No recommendation coverage rules specified for this provider.');
      }
    }
  }
}
